import {
  ClipboardDocumentIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import clsx from "clsx";
import { FieldError } from "components/Forms/FieldError.tsx";
import NewLabelWrapper, {
  type LabelProps,
} from "components/Forms/NewLabelWrapper";
import { Button } from "components/Theme/button";
import { Input } from "components/Theme/input";
import { useField } from "formik";
import type React from "react";
import { useState } from "react";

export interface KeyValuePair {
  key: string;
  value: string;
  id?: string;
}

interface KeyValueMapFieldProps {
  fieldName: string;
  label?: string;
  disabled?: boolean;
  className?: string;
}

interface PasteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (pairs: KeyValuePair[]) => void;
  disabled?: boolean;
}

function PasteModal({
  isOpen,
  onClose,
  onApply,
  disabled,
}: PasteModalProps): React.ReactElement | null {
  const [jsonText, setJsonText] = useState("");
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const handleApply = () => {
    try {
      const parsed = JSON.parse(jsonText);

      if (typeof parsed !== "object" || Array.isArray(parsed)) {
        setError("JSON must be an object (dictionary)");
        return;
      }

      const pairs: KeyValuePair[] = Object.entries(parsed).map(
        ([key, value]) => ({
          key,
          value: String(value),
          id: generateId(),
        }),
      );

      onApply(pairs);
      setJsonText("");
      setError("");
      onClose();
    } catch (err) {
      setError("Invalid JSON format");
    }
  };

  const handleClose = () => {
    setJsonText("");
    setError("");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl p-6 w-full max-w-2xl">
        <h3 className="text-lg font-semibold mb-4 text-zinc-950 dark:text-white">
          Paste JSON
        </h3>
        <textarea
          aria-label="JSON input"
          className={clsx(
            "w-full h-48 p-3 rounded-lg border font-mono text-sm",
            "text-zinc-950 dark:text-white bg-white dark:bg-zinc-800",
            "border-zinc-950/10 dark:border-white/10",
            "focus:outline-none focus:ring-2 focus:ring-blue-500",
            error && "border-red-500 dark:border-red-500",
          )}
          value={jsonText}
          onChange={(e) => {
            setJsonText(e.target.value);
            setError("");
          }}
          placeholder='{"key1": "value1", "key2": "value2"}'
          disabled={disabled}
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex gap-3 justify-end">
          <Button
            type="button"
            outline
            onClick={handleClose}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Button
            type="button"
            color="brandLight"
            onClick={handleApply}
            disabled={disabled}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}

const MAX_ROWS = 50;
let nextId = 0;
const generateId = () => `kv-${++nextId}-${Date.now()}`;

export default function KeyValueMapField(
  props: KeyValueMapFieldProps,
): React.ReactElement {
  const {
    fieldName,
    label = "Key-Value Map",
    disabled = false,
    className,
  } = props;
  const [field, meta, helpers] = useField<KeyValuePair[]>(fieldName);
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [isCopied, setIsCopied] = useState(false);

  // Ensure we always have an array
  const pairs = Array.isArray(field.value) ? field.value : [];

  // Check for duplicate keys
  const duplicateKeys = pairs.reduce((acc, pair, index) => {
    const duplicateIndex = pairs.findIndex(
      (p, i) => i !== index && p.key && p.key === pair.key,
    );
    if (duplicateIndex !== -1 && pair.key && !acc.includes(pair.key)) {
      acc.push(pair.key);
    }
    return acc;
  }, [] as string[]);

  const handleAddRow = () => {
    if (pairs.length >= MAX_ROWS) {
      setWarningMessage(`Maximum limit of ${MAX_ROWS} rows reached`);
      setTimeout(() => setWarningMessage(""), 3000);
      return;
    }

    const newPairs = [...pairs, { key: "", value: "", id: generateId() }];
    helpers.setValue(newPairs);
    helpers.setTouched(true);
    setWarningMessage("");
  };

  const handleDeleteRow = (index: number) => {
    const newPairs = pairs.filter((_, i) => i !== index);
    helpers.setValue(newPairs);
    helpers.setTouched(true);
  };

  const handleKeyChange = (index: number, newKey: string) => {
    const newPairs = [...pairs];
    newPairs[index] = { ...newPairs[index], key: newKey };
    helpers.setValue(newPairs);
    helpers.setTouched(true);
  };

  const handleValueChange = (index: number, newValue: string) => {
    const newPairs = [...pairs];
    newPairs[index] = { ...newPairs[index], value: newValue };
    helpers.setValue(newPairs);
    helpers.setTouched(true);
  };

  const handleCopyAsJSON = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      const jsonObject = Object.fromEntries(
        pairs.map((pair) => [pair.key, pair.value]),
      );
      const jsonString = JSON.stringify(jsonObject, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2500);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      setIsCopied(false);
    }
  };

  const handlePasteFromJSON = (newPairs: KeyValuePair[]) => {
    if (newPairs.length > MAX_ROWS) {
      setWarningMessage(
        `Pasted data exceeded maximum limit of ${MAX_ROWS} rows. Only first ${MAX_ROWS} rows were added.`,
      );
      helpers.setValue(newPairs.slice(0, MAX_ROWS));
      setTimeout(() => setWarningMessage(""), 5000);
    } else {
      helpers.setValue(newPairs);
      setWarningMessage("");
    }
    helpers.setTouched(true);
  };

  const labelProps: LabelProps = {
    label,
  };

  return (
    <>
      <NewLabelWrapper {...labelProps}>
        <div className={clsx("space-y-3", className)}>
          {pairs.length === 0 ? (
            <div className="text-sm text-zinc-500 dark:text-zinc-400 italic py-4">
              No entries. Click 'Add Row' to start.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-950/10 dark:divide-white/10">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-sm font-semibold text-zinc-950 dark:text-white">
                      Key
                    </th>
                    <th className="px-3 py-2 text-left text-sm font-semibold text-zinc-950 dark:text-white">
                      Value
                    </th>
                    <th className="px-3 py-2 w-16" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-950/5 dark:divide-white/5">
                  {pairs.map((pair, index) => (
                    <tr key={pair.id || index}>
                      <td className="px-3 py-2">
                        <Input
                          type="text"
                          value={pair.key}
                          onChange={(e) =>
                            handleKeyChange(index, e.target.value)
                          }
                          onBlur={() => helpers.setTouched(true)}
                          disabled={disabled}
                          invalid={!!(meta.error && meta.touched)}
                          aria-label={`Key ${index + 1}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="text"
                          value={pair.value}
                          onChange={(e) =>
                            handleValueChange(index, e.target.value)
                          }
                          onBlur={() => helpers.setTouched(true)}
                          disabled={disabled}
                          invalid={!!(meta.error && meta.touched)}
                          aria-label={`Value ${index + 1}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          type="button"
                          outline
                          size="sm"
                          onClick={() => handleDeleteRow(index)}
                          disabled={disabled}
                          aria-label="Delete row"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-3 flex-wrap">
            <Button
              type="button"
              color="brandLight"
              onClick={handleAddRow}
              disabled={disabled}
              size="sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Row
            </Button>

            <span className="relative inline-block">
              <Button
                type="button"
                outline
                onClick={handleCopyAsJSON}
                size="sm"
              >
                <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                Copy as JSON
              </Button>
              {isCopied && (
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-zinc-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap dark:bg-zinc-700">
                  Copied!
                </div>
              )}
            </span>

            <Button
              type="button"
              outline
              onClick={() => setPasteModalOpen(true)}
              disabled={disabled}
              size="sm"
            >
              <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
              Paste from JSON
            </Button>
          </div>

          <FieldError meta={meta} />

          {warningMessage && (
            <p className="mt-2 text-sm text-amber-600 dark:text-amber-500">
              {warningMessage}
            </p>
          )}

          {duplicateKeys.length > 0 && (
            <p className="mt-2 text-sm text-amber-600 dark:text-amber-500">
              Warning: Duplicate keys detected: {duplicateKeys.join(", ")}
            </p>
          )}
        </div>
      </NewLabelWrapper>

      <PasteModal
        isOpen={pasteModalOpen}
        onClose={() => setPasteModalOpen(false)}
        onApply={handlePasteFromJSON}
        disabled={disabled}
      />
    </>
  );
}
